/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChild, Fragment } from 'preact';
import { forwardRef, ForwardedRef } from 'preact/compat';
import { useRef, useImperativeHandle } from 'preact/hooks';

import { BaseButton } from '#UNSAFE_BaseButton';
import { useToggleAction, ToggleDetail } from '#hooks/UNSAFE_useToggleAction';
import { useTooltip } from '#hooks/UNSAFE_useTooltip';
import { TestIdProps } from '#hooks/UNSAFE_useTestId';
import type { DimensionProps } from '#utils/UNSAFE_interpolations/dimensions';
import { mergeProps } from '#utils/UNSAFE_mergeProps';

import { toggleStyling } from '#utils/UNSAFE_buttonUtils';
import { useId } from '#hooks/UNSAFE_useId';
import { ToggleButtonLabel } from '#UNSAFE_ToggleButtonLabel';
import type { ToggleButtonLabelProps } from '#UNSAFE_ToggleButtonLabel/ToggleButtonLabel';

type WidthInterpolationProps = Pick<DimensionProps, 'width'>;

export type IconSwitchButtonProps = WidthInterpolationProps &
  TestIdProps & {
    /**
     * icon - the icon for the button
     */
    children?: ComponentChild;

    /**
     * Specifies that the button element should be disabled.
     */
    isDisabled?: boolean;

    /**
     * The ID of an element (or space separated IDs of multiple elements) that
     * describes the button.
     */
    'aria-describedby'?: string;

    /**
     * Specifies if the toggle button is selected
     */
    isSelected?: boolean;

    /**
     * Property that triggers a callback immediately when toggle happens and value of isSelected property should be updated
     */
    onToggle?: (details: ToggleDetail) => void;

    /**
     * The size indicates how large the button is rendered: "xs", "sm", "md" (default), "lg".
     */
    size?: 'xs' | 'sm' | 'md' | 'lg';
  };

/**
 * Note that either an accessibleLabel or tooltip must be specified.
 */
type VariationAccessibleLabelProps =
  | {
      /**
       * accessibleLabel - the aria label
       */
      accessibleLabel: string;

      /**
       * tooltip - overrides the accessibleLabel for the tooltip
       */
      tooltip?: string;
    }
  | {
      accessibleLabel?: string;

      tooltip: string;
    };

/**
 * A IconSwitchButton is an icon button that allows users to switch
 * between states when pressed.  It differs from a toggle button in not
 * providing any visual indication of the state; instead, switch the
 * icon to provide the state indication.
 */
export const IconSwitchButton = forwardRef(
  (
    {
      children,
      tooltip,
      size = 'md',
      isDisabled = false,
      accessibleLabel,
      isSelected = false,
      onToggle,
      testId,
      width,
      'aria-describedby': ariaDescribedBy
    }: IconSwitchButtonProps & VariationAccessibleLabelProps & TestIdProps,
    ref?: ForwardedRef<HTMLButtonElement>
  ) => {
    const buttonRef = useRef<HTMLButtonElement>(null);
    useImperativeHandle(ref!, () => buttonRef.current!, [buttonRef]);

    const { triggerProps } = useToggleAction({
      isDisabled: isDisabled,
      onToggle: onToggle
    });

    const { tooltipContent, tooltipProps } = useTooltip({
      text: tooltip ?? accessibleLabel,
      isDisabled: isDisabled
    });

    const styling = toggleStyling(
      'auto',
      isSelected,
      true,
      false,
      false,
      undefined,
      undefined,
      true
    );

    const ariaProps = { 'aria-describedby': ariaDescribedBy };

    return (
      <Fragment>
        <BaseButton
          {...mergeProps(tooltipProps, triggerProps, ariaProps)}
          elementDetails={{ type: 'button', isSwitch: true }}
          aria-checked={isSelected}
          variant="ghost"
          size={size}
          isDisabled={isDisabled}
          styling={styling}
          width={width}
          ref={buttonRef}
          aria-label={accessibleLabel ?? tooltip}
          testId={testId}>
          <ToggleButtonLabel
            isDisabled={isDisabled}
            label={accessibleLabel ?? tooltip}
            isToggle={true}
            inputName={useId()}
            inputType={'switch'}
            onAction={() => onToggle?.({ value: isSelected })}
            isSelected={isSelected}
            display={'icons'}
            startIcon={children}
            size={size as ToggleButtonLabelProps['size']}
          />
        </BaseButton>
        {tooltipContent}
      </Fragment>
    );
  }
);

IconSwitchButton.displayName = 'IconSwitchButton';
